import React, { useState } from 'react';
import {
    COMPARISON_CASH_COMES_FROM_LIQUIDATION,
    createLiquidationPortfolioV2,
    createPortfolio,
    getEquityMultipleForSimpleYearlyReturn,
    Portfolio,
    YEARS
} from "../create-portfolio";
import { PortfolioYear } from "../types/syndication";
import {
    Typography,
    useTheme,
    Container,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Box,
    Paper
} from "@mui/material";
import Charts from "./Charts";
import Spacer from "../Spacer";
import { roundPercentage } from "../util";
import Footer from "../Footer";
import YearModal from './YearModal';

const DEFAULT_NAME = 'Investor';

interface PortfolioTableProps {
    portfolio: Portfolio;
    name?: string
}

const Emphasis: React.FC<React.PropsWithChildren> = ({ children }) => {
    const theme = useTheme();

    return (
        <Typography
            component="span"
            sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
            }}
        >
            {children}
        </Typography>
    );
}

const MetricTitle: React.FC<React.PropsWithChildren> = ({ children }) => {
    return (
        <Typography sx={{ fontWeight: 700, margin: 0 }}>{children}</Typography>
    )
}

const Parameter: React.FC<React.PropsWithChildren> = ({ children }) => {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 1,
                backgroundColor: theme.palette.grey[300],
                border: `1px solid ${theme.palette.grey[400]}`,
                padding: theme.spacing(1),
            }}
        >
            {children}
        </Paper>
    )
}

const getDelta = (reNumber: number, comparisonNumber: number) => {
    if (reNumber === comparisonNumber) {
        return 0; // handles 0/0
    }
    return roundPercentage(comparisonNumber / reNumber - 1);
}

const DeltaValue: React.FC<{ delta: number, invertColor?: boolean }> = ({ delta, invertColor = false }) => {
    const theme = useTheme();

    if (delta === 0) {
        return null;
    }

    const withInvert = delta * (invertColor ? -1 : 1);

    let color = theme.palette.grey[600];
    if (withInvert > 0) {
        color = theme.palette.success.main;
    }
    if (withInvert < 0) {
        color = theme.palette.error.main;
    }
    return (
        <Typography
            component="span"
            sx={{ color }}
        >
            ({delta > 0 ? '+' : ''}{delta}%)
        </Typography>
    )
}

const PortfolioTable: React.FC<PortfolioTableProps> = ({ portfolio, name }) => {
    const finalName = name?.trim() || DEFAULT_NAME;
    const [selectedYear, setSelectedYear] = useState<PortfolioYear | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });
    const theme = useTheme();

    const {
        investPerYear,
        cocReturn,
        equityMultiple,
        stopInvestingAtYear,
        stopReinvestingAtYear,
        comparisonInvestment,
        income,
        filingStatus,
        includeTaxes,
    } = portfolio.parameters;

    let comparisonPortfolio: PortfolioYear[];

    if (comparisonInvestment.cashDistributionsComeFrom === COMPARISON_CASH_COMES_FROM_LIQUIDATION) {
        comparisonPortfolio = createLiquidationPortfolioV2(
            investPerYear,
            comparisonInvestment.returnRate,
            comparisonInvestment.liquidationRate,
            stopInvestingAtYear,
            stopReinvestingAtYear,
            income,
            filingStatus,
            includeTaxes,
        );
    } else {
        const completeComparisonPortfolio = createPortfolio(
            investPerYear,
            comparisonInvestment.liquidationRate,
            getEquityMultipleForSimpleYearlyReturn(comparisonInvestment.returnRate),
            stopInvestingAtYear,
            stopReinvestingAtYear,
            {
                returnRate: 0,
                liquidationRate: 0,
                cashDistributionsComeFrom: COMPARISON_CASH_COMES_FROM_LIQUIDATION,
            },
            income,
            filingStatus,
            includeTaxes,
        );
        comparisonPortfolio = completeComparisonPortfolio.years;
    }

    const displayCoc = Math.round(cocReturn * 100 * 100) / 100;
    const lastYear: PortfolioYear | undefined = portfolio.years.slice(-1)[0];

    const getWhenStopReinvestingDistributionsText = () => {
        if (stopReinvestingAtYear === 0) {
            return 'immediately';
        }
        if (stopReinvestingAtYear === Infinity) {
            return 'never';
        }

        return `year ${stopReinvestingAtYear}`;
    }

    const getStopReinvestingText = (action: string, year: number) => {
        if (year === Infinity) {
            return `Never stopped ${action}`;
        }
        if (year === 0) {
            return `Never started ${action}`;
        }
        return `Stopped ${action} in year ${year}`
    }

    const handleYearClick = (year: PortfolioYear) => {
        setSelectedYear(year);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedYear(null);
    };

    return (
        <>
            <Container sx={{ marginTop: '80px' }}>
                <Box>
                    <Typography variant="h4">{finalName}'s {YEARS} year plan</Typography>
                    <Spacer size="four" vertical />
                </Box>
                <Box>
                    <Grid container spacing={2} sx={{ '@media print': { display: 'flex', flexWrap: 'wrap' } }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Parameter><MetricTitle>Amount invested per year:</MetricTitle> {formatter.format(investPerYear)}</Parameter>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Parameter><MetricTitle>Cash-on-cash-return:</MetricTitle> {displayCoc}%</Parameter>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Parameter><MetricTitle>Equity multiple:</MetricTitle> {equityMultiple}</Parameter>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Parameter><MetricTitle>{getStopReinvestingText(`investing ${formatter.format(investPerYear)}`, stopInvestingAtYear)}</MetricTitle></Parameter>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <Parameter><MetricTitle>{getStopReinvestingText('reinvesting distributions', stopReinvestingAtYear)}</MetricTitle></Parameter>
                        </Grid>
                    </Grid>
                    <Spacer size="three" vertical />
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.primary.main}`,
                        padding: theme.spacing(1),
                    }}
                >
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Your results:</Typography>
                    <Spacer size="two" vertical />
                    <Typography variant="body1">
                        By year 20, your equity position is <Emphasis>{formatter.format(lastYear?.totalEquity || 0)}</Emphasis> and your cash flow distributions are <Emphasis>{formatter.format(lastYear.passiveIncome || 0)}</Emphasis> per year!
                    </Typography>
                </Paper>
                <Box sx={{ textAlign: 'left' }}>
                    <Spacer size="four" vertical />
                    <Charts portfolio={portfolio} comparisonPortfolio={comparisonPortfolio} />
                    <Box sx={{ fontStyle: "italic" }}>
                        <Typography variant="body2">
                            The charts above compare your total equity and cash flow in real estate syndications to an alternative investment <i>(stocks, a high-yield savings account, etc)</i> that compounds at <Emphasis>{roundPercentage(portfolio.parameters.comparisonInvestment.returnRate)}%</Emphasis>.
                        </Typography>
                        <Typography variant="body2">
                            The passive income figure for the high-yield savings account comes from taking {roundPercentage(comparisonInvestment.liquidationRate)}% per year from equity, which in this simulation begins when you stop reinvesting syndication distributions (<Emphasis>{getWhenStopReinvestingDistributionsText()}</Emphasis>).
                        </Typography>
                    </Box>
                </Box>
                <Spacer size="four" vertical />
                <Table>
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell colSpan={2}>Total Equity</TableCell>
                            <TableCell colSpan={2}>Passive Income</TableCell>
                            {includeTaxes && <TableCell colSpan={2}>Taxes</TableCell>}
                        </TableRow>
                        <TableRow>
                            <TableCell>Year</TableCell>
                            <TableCell>Conveyor Belt Deals</TableCell>
                            <TableCell>Comparison</TableCell>
                            <TableCell>Conveyor Belt Deals</TableCell>
                            <TableCell>Comparison</TableCell>
                            {includeTaxes && (
                                <>
                                    <TableCell>Conveyor Belt Deals</TableCell>
                                    <TableCell>Comparison</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {portfolio && portfolio.years.map((year, idx) => {
                            const lastYear = portfolio.years[idx - 1];
                            const lastYearTaxes = lastYear?.taxes.getTotalSyndicationTaxes() || 0;

                            const lastYearComparison = comparisonPortfolio[idx - 1];
                            const lastYearComparisonTaxes = lastYearComparison?.taxes.getTotalSyndicationTaxes() || 0;

                            const reEquity = year.totalEquity || 0;
                            const rePassiveIncome = year.passiveIncome || 0;
                            const reDistributions = year.distributions || 0;
                            const reTaxes = year.taxes;
                            const comparisonEquity = comparisonPortfolio[idx].totalEquity || 0;
                            const comparisonPassiveIncome = comparisonPortfolio[idx].passiveIncome || 0;
                            const comparisonTotalLiquidation = comparisonPortfolio[idx].distributions || 0;
                            const comparisonTaxes = comparisonPortfolio[idx].taxes;
                            const comparisonEquityDelta = getDelta(reEquity, comparisonEquity);
                            const comparisonIncomeDelta = getDelta(rePassiveIncome, comparisonPassiveIncome);
                            const comparisonTaxesDelta = getDelta(reTaxes.getTotalTaxes(), comparisonTaxes.getTotalTaxes());
                            const reinvestingDistributionsThisYear = year.yearOneBasedIdx < stopReinvestingAtYear;

                            return (
                                <TableRow key={year.year}>
                                    <TableCell
                                        sx={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                        }}
                                        onClick={() => handleYearClick(year)}
                                    >
                                        {year.year}
                                    </TableCell>
                                    <TableCell>
                                        {formatter.format(reEquity)}
                                    </TableCell>
                                    <TableCell>
                                        {formatter.format(comparisonEquity)}
                                        <Spacer size="one" />
                                        <DeltaValue delta={comparisonEquityDelta} />
                                    </TableCell>
                                    <TableCell>
                                        {formatter.format(rePassiveIncome)}
                                        <div style={{ color: '#AAAAAA' }}>
                                            ({formatter.format(reDistributions)} distributed
                                            {lastYearTaxes > 0 && (
                                                <>
                                                    <Spacer size="one" />
                                                    - {formatter.format(lastYearTaxes)} taxes
                                                </>
                                            )}
                                            {reinvestingDistributionsThisYear && (
                                                <>
                                                    <Spacer size="one" />
                                                    - {formatter.format(reDistributions - lastYearTaxes)} reinvesting next year
                                                </>
                                            )}
                                            )
                                            {lastYear?.leftoverTaxesFromLastYear && lastYear.leftoverTaxesFromLastYear > 0 ? (
                                                <>
                                                    <Spacer size="one" />
                                                    {formatter.format(lastYear.leftoverTaxesFromLastYear)} leftover taxes
                                                </>
                                            ) : null}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {formatter.format(comparisonPassiveIncome)}
                                        <div style={{ color: '#AAAAAA' }}>
                                            ({formatter.format(comparisonTotalLiquidation)} liquidated
                                            {lastYearComparisonTaxes > 0 && (
                                                <>
                                                    <Spacer size="one" />
                                                    - {formatter.format(lastYearComparisonTaxes)} taxes
                                                </>
                                            )}
                                            )
                                        </div>
                                        <Spacer size="one" />
                                        {/* we don't liquidate the first year */}
                                        {idx > 0 && <DeltaValue delta={comparisonIncomeDelta} />}
                                    </TableCell>
                                    {includeTaxes && (
                                        <>
                                            <TableCell>
                                                <div>
                                                    <div>Passive: {formatter.format(reTaxes.ordinaryPassiveIncome)}</div>
                                                    <div>Capital Gains: {formatter.format(reTaxes.ltCapitalGains)}</div>
                                                    <div>Depreciation: {formatter.format(reTaxes.depreciationRecapture)}</div>
                                                    <div style={{ fontWeight: 'bold', marginTop: '4px' }}>
                                                        Total: {formatter.format(reTaxes.getTotalSyndicationTaxes())}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <div>Passive: {formatter.format(comparisonTaxes.ordinaryPassiveIncome)}</div>
                                                    <div>Capital Gains: {formatter.format(comparisonTaxes.ltCapitalGains)}</div>
                                                    <div>Depreciation: {formatter.format(comparisonTaxes.depreciationRecapture)}</div>
                                                    <div style={{ fontWeight: 'bold', marginTop: '4px' }}>
                                                        Total: {formatter.format(comparisonTaxes.getTotalSyndicationTaxes())}
                                                    </div>
                                                </div>
                                                <Spacer size="one" />
                                                <DeltaValue delta={comparisonTaxesDelta} invertColor={true} />
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {selectedYear && (
                    <YearModal
                        open={isModalOpen}
                        onClose={handleModalClose}
                        yearData={selectedYear}
                        lastYearData={portfolio.years.find(y => y.year === selectedYear.year - 1)}
                        equityMultiple={portfolio.parameters.equityMultiple}
                        cocReturn={portfolio.parameters.cocReturn}
                        includeTaxes={portfolio.parameters.includeTaxes}
                    />
                )}
            </Container>
            <Footer />
        </>
    );
};

export default PortfolioTable;